'use client'

/**
 * ProfielSlide — batch-71 profiel-in-beeld (FO-538): één slide van de profielschets op het
 * scherm, gedeeld door de backoffice-editor, het template-beheer en het klantportaal.
 *
 * Tekent de vormen uit de template-layout op hun plek: foto's, vrije vormen en tekst. Alle
 * maten staan in container-eenheden (`cqw`), dus de slide schaalt zonder JavaScript mee met
 * zijn kolom: van een duimnagel in de strook tot de volle breedte.
 *
 * Dit component rekent niets uit. Wat er in de tekstvakken staat (de regels, "+ n meer", de
 * verkleining) komt als kant-en-klaar `ProfielSlideModel` binnen van `profielSlideModel` uit
 * @streep/utils: dezelfde bron als de `.pptx`, zodat de app en het document hetzelfde tonen.
 * `@streep/ui` blijft daarmee vrij van `@streep/*`-afhankelijkheden.
 *
 * Lettertype: het ontwerp staat in Tahoma. Dat is geen webfont en wordt hier niet
 * meegeleverd (licentie, zie A3): de browser gebruikt het systeemlettertype, dat op Windows
 * en bij Office voor Mac aanwezig is. Op een ander toestel staat er een gelijkend lettertype;
 * de regels en de verkleining blijven dezelfde want die zijn al berekend.
 */
import { useState, type CSSProperties, type ReactNode } from 'react'

export interface ProfielSlideRun {
  tekst: string
  pt: number
  vet: boolean
  cursief: boolean
  kleur: string | null
  lettertype: string | null
  spatiePt: number
}

export interface ProfielSlideParagraaf {
  sleutel: string
  token: string | null
  inlineToken: string | null
  regel: {
    soort: string
    sleutel?: string
    checked?: boolean
    bronIndex?: number
  } | null
  runs: ProfielSlideRun[]
  regelafstandPt: number
  voorPt: number
  naPt: number
  margeLinksPt: number
  inspringPt: number
  uitlijning: 'links' | 'midden' | 'rechts'
  leegPt: number
}

interface ProfielSlideVormBasis {
  id: number
  naam: string
  x: number
  y: number
  cx: number
  cy: number
}

export interface ProfielSlideFotoVorm extends ProfielSlideVormBasis {
  soort: 'foto'
  media: string | null
  bijsnijding: { l: number; t: number; r: number; b: number }
  isProfielFoto: boolean
  masker: Array<[number, number]> | null
}

export interface ProfielSlideVrijeVorm extends ProfielSlideVormBasis {
  soort: 'vrijevorm'
  pad: string
  padBreedte: number
  padHoogte: number
  vulling: string | null
  lijn: { kleur: string; breedtePt: number } | null
}

export interface ProfielSlideTekstVorm extends ProfielSlideVormBasis {
  soort: 'tekst'
  terugloop: boolean
  marges: { l: number; t: number; r: number; b: number }
  paragrafen: ProfielSlideParagraaf[]
  tokens: string[]
}

export type ProfielSlideVorm = ProfielSlideFotoVorm | ProfielSlideVrijeVorm | ProfielSlideTekstVorm

export interface ProfielSlideModel {
  nummer: number
  breedteEmu: number
  hoogteEmu: number
  achtergrond: string | null
  vormen: ProfielSlideVorm[]
}

export interface ProfielSlideBijsnijding {
  l: number
  t: number
  r: number
  b: number
}

export interface ProfielSlideProps {
  model: ProfielSlideModel
  /** De URL van een afbeelding uit de template (bestandsnaam binnen `ppt/media/`). */
  mediaUrl: (naam: string) => string
  /** De medewerkerfoto; null/undefined = het lege kader uit de template. */
  fotoUrl?: string | null
  /** De bijsnijding op basis van de echte fotomaten (`berekenProfielBijsnijding`). */
  fotoBijsnijding?: (
    fotoBreedte: number,
    fotoHoogte: number,
    kaderBreedte: number,
    kaderHoogte: number,
  ) => ProfielSlideBijsnijding
  /** Maak een tekstvorm klikbaar of vervang hem door een editor. */
  renderTekstVorm?: (vorm: ProfielSlideTekstVorm, inhoud: ReactNode) => ReactNode
  /** Maak één paragraaf klikbaar (een vinkje). */
  renderParagraaf?: (
    paragraaf: ProfielSlideParagraaf,
    vorm: ProfielSlideTekstVorm,
    inhoud: ReactNode,
  ) => ReactNode
  /** Maak de medewerkerfoto klikbaar. */
  renderFoto?: (vorm: ProfielSlideFotoVorm, inhoud: ReactNode) => ReactNode
  className?: string
  /** Toegankelijke naam van de slide. */
  label?: string
}

/** Lettertype-stapel per ontwerp-font; onbekend = de naam zelf met een terugval. */
function fontStapel(lettertype: string | null): string {
  if (lettertype === 'Tahoma') return 'Tahoma, Verdana, "DejaVu Sans", sans-serif'
  if (lettertype === 'Segoe UI Symbol') return '"Segoe UI Symbol", "Apple Symbols", "DejaVu Sans", sans-serif'
  if (!lettertype) return 'Tahoma, Verdana, sans-serif'
  return `"${lettertype}", Tahoma, Verdana, sans-serif`
}

function rand(emu: number, totaal: number): string {
  return `${(emu / totaal) * 100}%`
}

function run(run: ProfielSlideRun, schaal: (pt: number) => string, index: number) {
  return (
    <span
      key={index}
      style={{
        fontSize: schaal(run.pt),
        fontWeight: run.vet ? 700 : 400,
        fontStyle: run.cursief ? 'italic' : 'normal',
        color: run.kleur ?? '#000000',
        fontFamily: fontStapel(run.lettertype),
        letterSpacing: run.spatiePt ? schaal(run.spatiePt) : undefined,
      }}
    >
      {run.tekst}
    </span>
  )
}

/** De medewerkerfoto: bijgesneden op de echte maten, in het schuine kader. */
function FotoKader({
  url,
  vorm,
  bijsnijding,
}: {
  url: string
  vorm: ProfielSlideFotoVorm
  bijsnijding?: ProfielSlideProps['fotoBijsnijding']
}) {
  const [maten, setMaten] = useState<{ b: number; h: number } | null>(null)
  const crop = maten && bijsnijding ? bijsnijding(maten.b, maten.h, vorm.cx, vorm.cy) : null
  const stijl: CSSProperties = crop
    ? {
        position: 'absolute',
        maxWidth: 'none',
        width: `${100 / (1 - crop.l - crop.r)}%`,
        height: `${100 / (1 - crop.t - crop.b)}%`,
        left: `${(-crop.l / (1 - crop.l - crop.r)) * 100}%`,
        top: `${(-crop.t / (1 - crop.t - crop.b)) * 100}%`,
      }
    : { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 35%' }
  return (
    <img
      src={url}
      alt=""
      style={stijl}
      onLoad={(e) => setMaten({ b: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
    />
  )
}

export function ProfielSlide({
  model,
  mediaUrl,
  fotoUrl,
  fotoBijsnijding,
  renderTekstVorm,
  renderParagraaf,
  renderFoto,
  className,
  label,
}: ProfielSlideProps) {
  const breedtePt = model.breedteEmu / 12700
  const schaal = (pt: number) => `calc(${pt} * 100cqw / ${breedtePt})`
  const positie = (v: ProfielSlideVormBasis): CSSProperties => ({
    position: 'absolute',
    left: rand(v.x, model.breedteEmu),
    top: rand(v.y, model.hoogteEmu),
    width: rand(v.cx, model.breedteEmu),
    height: rand(v.cy, model.hoogteEmu),
  })

  return (
    <div
      className={className}
      role="img"
      aria-label={label ?? `Slide ${model.nummer}`}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: `${model.breedteEmu} / ${model.hoogteEmu}`,
        overflow: 'hidden',
        background: model.achtergrond ?? '#FFFFFF',
        containerType: 'inline-size',
      }}
    >
      {model.vormen.map((vorm, index) => {
        const sleutel = `${vorm.soort}-${vorm.id}-${index}`
        if (vorm.soort === 'foto') {
          const masker = vorm.masker
            ? `polygon(${vorm.masker.map(([x, y]) => `${x * 100}% ${y * 100}%`).join(', ')})`
            : undefined
          const { l, t, r, b } = vorm.bijsnijding
          const inhoud =
            vorm.isProfielFoto && fotoUrl ? (
              <FotoKader url={fotoUrl} vorm={vorm} bijsnijding={fotoBijsnijding} />
            ) : vorm.media ? (
              <img
                src={mediaUrl(vorm.media)}
                alt=""
                style={{
                  position: 'absolute',
                  maxWidth: 'none',
                  width: `${100 / (1 - l - r)}%`,
                  height: `${100 / (1 - t - b)}%`,
                  left: `${(-l / (1 - l - r)) * 100}%`,
                  top: `${(-t / (1 - t - b)) * 100}%`,
                }}
              />
            ) : null
          const kader = (
            <div key={sleutel} style={{ ...positie(vorm), overflow: 'hidden', clipPath: masker }}>
              {inhoud}
            </div>
          )
          return vorm.isProfielFoto && renderFoto ? (
            <div key={sleutel} style={{ display: 'contents' }}>
              {renderFoto(vorm, kader)}
            </div>
          ) : (
            kader
          )
        }
        if (vorm.soort === 'vrijevorm') {
          return (
            <svg
              key={sleutel}
              style={positie(vorm)}
              viewBox={`0 0 ${vorm.padBreedte} ${vorm.padHoogte}`}
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path
                d={vorm.pad}
                fill={vorm.vulling ?? 'none'}
                stroke={vorm.lijn?.kleur ?? 'none'}
                strokeWidth={vorm.lijn ? (vorm.lijn.breedtePt * vorm.padBreedte) / (vorm.cx / 12700) : 0}
              />
            </svg>
          )
        }
        const inhoud = (
          <div
            key={sleutel}
            style={{
              position: 'absolute',
              left: rand(vorm.x, model.breedteEmu),
              top: rand(vorm.y, model.hoogteEmu),
              width: vorm.terugloop ? rand(vorm.cx, model.breedteEmu) : undefined,
              paddingLeft: schaal(vorm.marges.l),
              paddingTop: schaal(vorm.marges.t),
              paddingRight: schaal(vorm.marges.r),
              paddingBottom: schaal(vorm.marges.b),
              whiteSpace: vorm.terugloop ? 'pre-wrap' : 'pre',
            }}
          >
            {vorm.paragrafen.map((p) => {
              const regel = (
                <div
                  key={p.sleutel}
                  style={{
                    lineHeight: schaal(p.regelafstandPt),
                    minHeight: schaal(p.regelafstandPt),
                    fontSize: schaal(p.leegPt),
                    marginTop: p.voorPt ? schaal(p.voorPt) : undefined,
                    marginBottom: p.naPt ? schaal(p.naPt) : undefined,
                    marginLeft: p.margeLinksPt ? schaal(p.margeLinksPt) : undefined,
                    textIndent: p.inspringPt ? schaal(p.inspringPt) : undefined,
                    textAlign: p.uitlijning === 'rechts' ? 'right' : p.uitlijning === 'midden' ? 'center' : 'left',
                  }}
                >
                  {p.runs.map((r, i) => run(r, schaal, i))}
                </div>
              )
              return renderParagraaf ? (
                <div key={p.sleutel}>{renderParagraaf(p, vorm, regel)}</div>
              ) : (
                regel
              )
            })}
          </div>
        )
        return renderTekstVorm ? <div key={sleutel}>{renderTekstVorm(vorm, inhoud)}</div> : inhoud
      })}
    </div>
  )
}
